/**
 * Drill-down review for one Capture_Batch.
 *
 * The operator steps through each unconfirmed image, reviews the
 * prediction, then either:
 *   - confirms the prediction (only enabled when `identification.status ===
 *     'known'`); or
 *   - corrects the identity, supplying a fresh {@link CardIdentity}.
 *
 * On confirm the API persists a Card_Record from the device-submitted
 * image (Requirement 12.13). Confirm is gated server-side too, but the UI
 * mirrors the gate so reviewers don't get surprise 409s.
 *
 * Validates: Requirement 12.12
 */

import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { EMPTY_IDENTITY, IdentityForm } from '../shared/IdentityForm';
import { PredictionView, formatIdentity } from '../shared/PredictionView';
import type { CaptureBatch, CardIdentity, SessionImage } from '../shared/types';
import { LabelTile } from '../storage/LabelTile';
import { printSingleLabel } from '../storage/label-print';
import { useStorageApi } from '../storage/storage-api';
import { useBatchesApi } from './batches-api';
import { mapResolutionOutcome, usableRunId, type RunLabelState } from './run-label';
import { useAuth } from '../auth/AuthContext';
import { hasAnyGroup } from '../auth/cognito';

type Mode = 'idle' | 'correcting';

export function BatchReviewPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const api = useBatchesApi();
  const { claims } = useAuth();
  const isAdmin = claims !== null && hasAnyGroup(claims, ['admin']);
  const [batch, setBatch] = useState<CaptureBatch | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [step, setStep] = useState(0);
  const [mode, setMode] = useState<Mode>('idle');
  const [draft, setDraft] = useState<CardIdentity>(EMPTY_IDENTITY);

  const refresh = useCallback(async () => {
    if (id === undefined) return;
    const result = await api.get(id);
    if (!result.ok) {
      setError(result.message ?? result.error);
      return;
    }
    setBatch(result.data);
  }, [api, id]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Admin-only retirement removes the capture batch from active review while
  // preserving the produced card record and images for restoration.
  const onDelete = async () => {
    if (id === undefined) return;
    if (
      !window.confirm(
        'Retire the card produced by this batch and remove the capture batch from review? Its record and images remain recoverable.',
      )
    ) {
      return;
    }
    const result = await api.reject(id, { deleteCard: true });
    if (!result.ok) {
      setError(result.message ?? result.error);
      return;
    }
    navigate('/batches');
  };

  if (error !== null) return <p className="tcvs-error">Error: {error}</p>;
  if (batch === null) return <p>Loading…</p>;

  const pending = batch.images.filter((img) => !img.confirmed);
  if (pending.length === 0) {
    return (
      <div>
        <h2>Batch {batch.batchId}</h2>
        <p>All images in this batch have been reviewed.</p>
        {isAdmin ? (
          <button type="button" onClick={() => void onDelete()} className="tcvs-btn-danger">
            Delete batch
          </button>
        ) : null}
      </div>
    );
  }

  const current = pending[Math.min(step, pending.length - 1)];
  if (current === undefined) return <p>No image to review.</p>;

  const onConfirm = async () => {
    if (id === undefined) return;
    const result = await api.confirm(id, current.imageId);
    if (!result.ok) {
      setError(result.message ?? result.error);
      return;
    }
    setStep((s) => s + 1);
    void refresh();
  };

  const onSubmitCorrection = async () => {
    if (id === undefined) return;
    const result = await api.correct(id, current.imageId, draft);
    if (!result.ok) {
      setError(result.message ?? result.error);
      return;
    }
    setMode('idle');
    setDraft(EMPTY_IDENTITY);
    setStep((s) => s + 1);
    void refresh();
  };

  // Dismiss is a non-destructive "hide from queue" action available only while
  // the batch is still in a review state (AWAITING_REVIEW / PARTIALLY_CONFIRMED).
  // CONFIRMED / CANCELED / DISMISSED batches do not show the control.
  const canDismiss = batch.state === 'AWAITING_REVIEW' || batch.state === 'PARTIALLY_CONFIRMED';

  const onDismiss = async () => {
    if (id === undefined) return;
    const result = await api.dismiss(id);
    if (!result.ok) {
      setError(result.message ?? result.error);
      return;
    }
    navigate('/batches');
  };

  return (
    <div>
      <h2>Batch {batch.batchId}</h2>
      <p>
        Device: {batch.deviceId} · Submitted: {batch.submittedAt} · {pending.length} image
        {pending.length === 1 ? '' : 's'} remaining
      </p>
      {canDismiss ? (
        <button type="button" onClick={() => void onDismiss()} style={{ marginBottom: 12 }}>
          Dismiss batch
        </button>
      ) : null}
      {isAdmin ? (
        <button
          type="button"
          onClick={() => void onDelete()}
          className="tcvs-btn-danger"
          style={{ marginBottom: 12, marginLeft: canDismiss ? 8 : 0 }}
        >
          Delete batch
        </button>
      ) : null}
      <ImageStep image={current} />
      {mode === 'idle' ? (
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            type="button"
            disabled={current.identification.status !== 'known'}
            onClick={() => void onConfirm()}
            title={
              current.identification.status === 'known'
                ? 'Persist Card_Record using the predicted identity'
                : 'Predicted identity is unknown — use Correct to supply one.'
            }
          >
            Confirm prediction
          </button>
          <button
            type="button"
            onClick={() => {
              setMode('correcting');
              setDraft(
                current.identification.status === 'known'
                  ? current.identification.identity
                  : EMPTY_IDENTITY,
              );
            }}
          >
            Correct identity
          </button>
        </div>
      ) : (
        <div>
          <IdentityForm value={draft} onChange={setDraft} />
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <button type="button" onClick={() => void onSubmitCorrection()}>
              Submit correction
            </button>
            <button type="button" onClick={() => setMode('idle')}>
              Cancel
            </button>
          </div>
        </div>
      )}
      <RunLabelPanel runId={batch.runId} />
    </div>
  );
}

function ImageStep({ image }: { image: SessionImage }): JSX.Element {
  return (
    <article>
      <h3>Image {image.imageId.slice(0, 8)}</h3>
      {image.imageUrl !== undefined ? (
        <img
          src={image.imageUrl}
          alt={`Image ${image.imageId}`}
          style={{
            width: '100%',
            maxWidth: 504,
            maxHeight: 704,
            height: 'auto',
            border: '1px solid var(--tcvs-image-frame)',
            borderRadius: 4,
            display: 'block',
            marginBottom: 12,
          }}
        />
      ) : (
        <p className="tcvs-muted">(no image preview)</p>
      )}
      <p>
        Predicted:{' '}
        {image.identification.status === 'known'
          ? formatIdentity(image.identification.identity)
          : 'unknown'}
      </p>
      <PredictionView identification={image.identification} />
    </article>
  );
}

/**
 * Self-contained "Print this run's label" panel for the BatchReviewPage.
 *
 * Driven by the loaded Capture_Batch's `runId`. The panel — and its action — are
 * rendered only when `usableRunId(runId)` yields a defined run id, i.e. a
 * non-empty/non-whitespace string (Req 4.1); for a `null` or `undefined` runId
 * the panel renders nothing (Req 4.2).
 *
 * On activation it resolves the run's Storage_Batch via
 * `useStorageApi().resolveRunBatch` over the existing Cognito bearer-token
 * plumbing (Req 4.3), showing a loading indicator while the request is in flight
 * (Req 4.6). The discriminated {@link RunLabelState} (from `./run-label`) maps
 * each outcome to exactly one surface:
 *   - `resolved` → render the QR label via {@link LabelTile} inside the existing
 *     `.tcvs-print-label` container with the entityId shown, plus a **Print**
 *     button that invokes label print mode (Req 4.4, 4.5);
 *   - `not-found` → "This run has no Storage_Batch label yet." and no label
 *     (Req 5.1);
 *   - `error` → the message plus a working **Retry** control that re-issues the
 *     request (Req 5.2).
 *
 * The panel is a discrete section, so the rest of the review screen (image
 * stepper, Confirm/Correct) stays mounted and interactive throughout (Req 5.3).
 *
 * Validates: Requirements 4.1, 4.2, 4.3, 4.4, 4.5, 4.6, 5.1, 5.2, 5.3
 */
function RunLabelPanel({ runId }: { runId: string | null | undefined }): JSX.Element | null {
  const api = useStorageApi();
  const [state, setState] = useState<RunLabelState>({ status: 'idle' });

  const resolvable = usableRunId(runId);

  const resolve = useCallback(async () => {
    if (resolvable === undefined) return;
    setState({ status: 'loading' });
    const result = await api.resolveRunBatch(resolvable);
    setState(mapResolutionOutcome(result));
  }, [api, resolvable]);

  // Hidden entirely for null / undefined / whitespace runId (Req 4.2).
  if (resolvable === undefined) return null;

  return (
    <section className="tcvs-card" style={{ marginTop: 16 }}>
      <h3 style={{ marginTop: 0 }}>Run label</h3>
      <p className="tcvs-caption">Print the QR label for this run&apos;s Storage_Batch.</p>
      {state.status === 'idle' ? (
        <button type="button" onClick={() => void resolve()}>
          Print this run&apos;s label
        </button>
      ) : null}
      {state.status === 'loading' ? (
        <p className="tcvs-muted" style={{ margin: 0 }}>
          Loading run label…
        </p>
      ) : null}
      {state.status === 'not-found' ? (
        <p className="tcvs-muted" style={{ margin: 0 }}>
          This run has no Storage_Batch label yet.
        </p>
      ) : null}
      {state.status === 'error' ? (
        <div>
          <p className="tcvs-error">Error: {state.message}</p>
          <button type="button" onClick={() => void resolve()}>
            Retry
          </button>
        </div>
      ) : null}
      {state.status === 'resolved' ? (
        <LabelTile token={state.data.token} entityId={state.data.entityId}>
          <button type="button" onClick={printSingleLabel}>
            Print
          </button>
        </LabelTile>
      ) : null}
    </section>
  );
}
